// Ana görev ÇOKLU SEÇİM + toplu işlem hook'u. Üç arayüz de (Detaylı, Kolay,
// Profesyonel) bunu kullanır: seçim durumu + tek noktadan toplu yürütme.
//   const bulk = useTaskBulk({ numberFor: (id) => numById[id],
//                              refresh: () => { load(); notifyTasksChanged(); } });
import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { confirmDialog } from "./confirm";
import { runBulkTaskAction } from "./taskBulkActions";
import { kopyaGeriAlBildirimi } from "./bulkCopyUndo";

const LABELS = {
  done: "tamamlandı",
  paused: "beklemeye alındı",
  pending: "aktif yapıldı",
  overdue: "tarihi geçti işaretlendi",
  archive: "arşivlendi",
  pin: "sıra numarası sabitlendi",
  unpin: "sabiti kaldırıldı",
  move: "iş koluna taşındı",
  delete: "çöp kutusuna taşındı",
  cancel: "iptal edildi",
  "digest-mute": "sabah özetinden çıkarıldı",
  "reset-size": "kart boyutu sıfırlandı",
  reassign: "devredildi",
  reminder: "hatırlatması kuruldu",
  "due-soon": "yaklaşan uyarısı güncellendi",
  duplicate: "kopyalandı",
  shares: "paylaşıma eklendi",
  locks: "kilit ayarı güncellendi",
};

// Yıkıcı işlemler için onay metinleri.
const CONFIRMS = {
  delete: { title: "TOPLU SİL", msg: (n) => `${n} görev çöp kutusuna taşınsın mı?`, ok: "SİL" },
  cancel: { title: "TOPLU İPTAL", msg: (n) => `${n} görev iptal edilsin mi?`, ok: "İPTAL ET" },
  locks: {
    title: "TOPLU KİLİT AYARI",
    msg: (n) => `${n} görevin kilit ayarı değiştirilecek, onaylıyor musunuz?`,
    ok: "UYGULA",
  },
  shares: {
    title: "TOPLU PAYLAŞIM",
    msg: (n) =>
      `${n} göreve seçtiğiniz kişi ve izinler EKLENECEK.\nBu görevlerdeki diğer kişilerin mevcut izinleri SİLİNMEZ (birleştirme).`,
    ok: "UYGULA",
  },
  duplicate: { title: "TOPLU KOPYALA", msg: (n) => `${n} görevin kopyası oluşturulacak, onaylıyor musunuz?`, ok: "KOPYALA" },
};

export function useTaskBulk({ numberFor, titleFor, refresh, onEditQueue } = {}) {
  const [selectMode, setSelectMode] = useState(false);
  const [ids, setIds] = useState([]);
  const inflight = useRef(false); // çift gönderim kilidi

  const has = useCallback((id) => ids.includes(id), [ids]);
  const toggle = useCallback(
    (id) => setIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id])),
    [],
  );
  const start = useCallback((id) => { setSelectMode(true); setIds(id != null ? [id] : []); }, []);
  const selectAll = useCallback((allIds) => setIds(allIds || []), []);
  const clear = useCallback(() => setIds([]), []);
  const exit = useCallback(() => { setSelectMode(false); setIds([]); }, []);

  // Seçim modunda Escape → çıkış (seçim temizlenir).
  useEffect(() => {
    if (!selectMode) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // Bir modal açıkken Escape modalı kapatır; seçim modu korunur.
      if (document.querySelector('[data-testid="edit-task-modal"], [data-testid="confirm-modal"]')) return;
      exit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectMode, exit]);

  const runAction = useCallback(
    async (action, extra) => {
      if (inflight.current) return; // çift tıklama → tek uygulama
      const targetIds = ids;
      if (!targetIds.length) return;
      inflight.current = true;
      try {
        // AŞAMA 2 — sıralı düzenleme: backend'e toplu istek gitmez, kuyruk açılır.
        if (action === "edit") {
          onEditQueue?.(targetIds);
          return;
        }
        const onay = CONFIRMS[action];
        if (onay) {
          const cokFazla = action === "duplicate" && targetIds.length >= 50;
          const ok = await confirmDialog({
            title: onay.title,
            message: cokFazla
              ? `DİKKAT: ${targetIds.length} yeni kopya oluşacak!\n${onay.msg(targetIds.length)}`
              : onay.msg(targetIds.length),
            confirmText: onay.ok,
            cancelText: "VAZGEÇ",
            danger: true,
          });
          if (!ok) return;
        }
        const results = await runBulkTaskAction(targetIds, action, {
          numberFor,
          categoryId: extra?.categoryId,
          userId: extra?.userId,
          reminderAt: extra?.reminderAt,
          intervalMin: extra?.intervalMin,
          repeatLeft: extra?.repeatLeft,
          repeatTotal: extra?.repeatTotal,
          patch: extra?.patch,
          copyOpts: extra?.copyOpts,
          shares: extra?.shares,
          sharesFor: extra?.sharesFor,
          notify: extra?.notify,
          flags: extra?.flags,
          requiresOtp: extra?.requiresOtp,
        });
        // Kısmi başarısızlık: kilitli (423/403) ile gerçek hata ayrı raporlanır.
        const kilitli = [];
        const hatali = [];
        results.forEach((r, i) => {
          if (r.status === "fulfilled") return;
          const s = r.reason?.response?.status;
          (s === 423 || s === 403 ? kilitli : hatali).push(targetIds[i]);
        });
        const okCount = results.length - kilitli.length - hatali.length;
        if (refresh) refresh();
        if (action === "reset-size") {
          window.dispatchEvent(new CustomEvent("sertex:task-size-reset", { detail: { ids: targetIds } }));
        }
        exit();
        const ad = (id) => titleFor?.(id) || `#${numberFor?.(id) ?? id.slice(0, 6)}`;
        if (kilitli.length || hatali.length) {
          const p = [`${okCount} görev ${LABELS[action] || "güncellendi"}`];
          if (kilitli.length) p.push(`${kilitli.length} atlandı (kilitli): ${kilitli.map(ad).join(", ")}`);
          if (hatali.length) p.push(`${hatali.length} başarısız: ${hatali.map(ad).join(", ")}`);
          toast.error(p.join(" · "), { duration: 9000 });
        } else {
          const mesaj = `${okCount} görev ${LABELS[action] || "güncellendi"}`;
          // KOPYALA → bildirimde satır içi GERİ AL (bu kopyalara özel).
          if (action === "duplicate") {
            const yeniIds = results
              .filter((r) => r.status === "fulfilled" && r.value?.id)
              .map((r) => r.value.id);
            if (yeniIds.length) {
              kopyaGeriAlBildirimi(yeniIds, mesaj, refresh);
              return;
            }
          }
          toast.success(mesaj);
        }
      } finally {
        inflight.current = false;
      }
    },
    [ids, numberFor, titleFor, refresh, exit, onEditQueue],
  );

  return { selectMode, ids, has, toggle, start, selectAll, clear, exit, runAction };
}
